import { useMutation } from '@tanstack/react-query'
import { ViewShift } from '@xeetr/types/models'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { invalidateShiftQueries } from '../lib/mutationHelpers'
import { ShiftService } from '../services/Shift.service'

import { useTenantRequiredService } from './common/useServiceFactory'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseShiftsMutationReturn {
  createShift: (shift: ViewShift) => void
  updateShift: (params: { id: string; shift: Partial<ViewShift> }) => void
  deleteShift: (id: string) => void
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
}

/**
 * Hook for managing shift mutations (create, update, delete).
 *
 * Features:
 * - Granular PATCH updates for individual shifts
 * - Optimistic cache updates
 * - Automatic cache invalidation
 * - Toast notifications for success/error
 *
 * @example
 * ```tsx
 * const { updateShift, isUpdating } = useShiftsMutation()
 *
 * updateShift({
 *   id: 'shift-123',
 *   shift: { name: 'Updated Name' }
 * })
 * ```
 */
export const useShiftsMutation = (): UseShiftsMutationReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const getService = useTenantRequiredService(ShiftService)

  const createMutation = useMutation({
    mutationFn: async (shift: ViewShift) => {
      const service = await getService()
      return service.createShift(shift)
    },
    onSuccess: () => {
      invalidateShiftQueries(tenantId)
      showToast({ severity: 'success', detail: 'Schicht erfolgreich erstellt.' })
    },
    onError: (error) => {
      console.error('Shift create error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Erstellen der Schicht.',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, shift }: { id: string; shift: Partial<ViewShift> }) => {
      const service = await getService()
      return service.patchShift(id, shift)
    },
    onSuccess: () => {
      invalidateShiftQueries(tenantId)
      showToast({ severity: 'success', detail: 'Schicht erfolgreich aktualisiert.' })
    },
    onError: (error) => {
      console.error('Shift update error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Aktualisieren der Schicht.',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const service = await getService()
      return service.deleteShift(id)
    },
    onSuccess: () => {
      invalidateShiftQueries(tenantId)
      showToast({ severity: 'success', detail: 'Schicht erfolgreich gelöscht.' })
    },
    onError: (error) => {
      console.error('Shift delete error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Löschen der Schicht.',
      })
    },
  })

  return {
    createShift: createMutation.mutate,
    updateShift: updateMutation.mutate,
    deleteShift: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
